// src/pages/ObservabilityPage.js
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Stack,
    Button,
    Divider,
    LinearProgress,
    CircularProgress,
    Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { adminGet } from '../config/api';

const intFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const decimalFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });
const percentFormatter = new Intl.NumberFormat('fr-FR', { style: 'percent', maximumFractionDigits: 1 });
const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
});

const StatLine = ({ label, value, hint }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ py: 0.4 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography variant="body1" fontWeight={700}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Stack>
    </Stack>
);

const ObservabilityCard = ({ title, children }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2.5,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.1)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
        }}
    >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {title}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.5}>{children}</Stack>
    </Paper>
);

export default function ObservabilityPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [updatedAt, setUpdatedAt] = useState(null);

    const loadMetrics = useCallback(
        (withSpinner) => {
            if (withSpinner) {
                setLoading(true);
            }
            setError('');
            adminGet('/metrics/summary')
                .then((json) => {
                    setData(json);
                    setUpdatedAt(new Date());
                })
                .catch((err) => {
                    setError(err?.message || 'Impossible de récupérer les métriques.');
                })
                .finally(() => {
                    if (withSpinner) {
                        setLoading(false);
                    }
                });
        },
        []
    );

    useEffect(() => {
        loadMetrics(true);
        const interval = setInterval(() => loadMetrics(false), 60000);
        return () => clearInterval(interval);
    }, [loadMetrics]);

    const mailjetSuccessRate = data?.mailjet ? data.mailjet.successRate : 0;
    const formattedUpdatedAt = useMemo(
        () => (updatedAt ? dateFormatter.format(updatedAt) : null),
        [updatedAt]
    );

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, margin: '0 auto' }}>
            <Stack spacing={2.5}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, md: 3.5 },
                        borderRadius: 4,
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        background:
                            'radial-gradient(circle at top left, rgba(79, 70, 229, 0.12), transparent 45%), linear-gradient(135deg, #f5f7ff 0%, #ffffff 45%, #f8fafc 100%)',
                    }}
                >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                        <QueryStatsIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                        <Box flex={1}>
                            <Typography variant="overline" sx={{ letterSpacing: '0.2em', color: 'primary.main' }}>
                                Observabilité
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900, mt: 0.5, letterSpacing: '-0.03em' }}>
                                Vue globale des signaux critiques
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary', maxWidth: 720 }}>
                                Suis les blocages de rate limiting, l’état des emails Mailjet, les appels Gemini et la
                                latence HTTP. Les données sont rafraîchies automatiquement toutes les 60 secondes.
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={() => loadMetrics(true)}
                                disabled={loading}
                            >
                                Rafraîchir
                            </Button>
                        </Stack>
                    </Stack>
                    {formattedUpdatedAt ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                            Dernière mise à jour : {formattedUpdatedAt}
                        </Typography>
                    ) : null}
                </Paper>

                {error ? <Alert severity="error">{error}</Alert> : null}

                {!data && loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : null}

                {data ? (
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={6}>
                            <ObservabilityCard title="Rate limiting">
                                <StatLine
                                    label="Total 429 émis"
                                    value={intFormatter.format(data.rateLimit?.blockedTotal ?? 0)}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Basé sur le filtre Bucket4J en frontal API.
                                </Typography>
                            </ObservabilityCard>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ObservabilityCard title="HTTP global">
                                <StatLine
                                    label="Requêtes totales"
                                    value={intFormatter.format(data.http?.totalRequests ?? 0)}
                                />
                                <StatLine
                                    label="4xx"
                                    value={intFormatter.format(data.http?.clientErrors ?? 0)}
                                />
                                <StatLine
                                    label="5xx"
                                    value={intFormatter.format(data.http?.serverErrors ?? 0)}
                                />
                                <StatLine
                                    label="Temps moyen"
                                    value={`${decimalFormatter.format(data.http?.meanDurationMs ?? 0)} ms`}
                                />
                                <StatLine
                                    label="p95"
                                    value={`${decimalFormatter.format(data.http?.p95DurationMs ?? 0)} ms`}
                                />
                            </ObservabilityCard>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ObservabilityCard title="Mailjet">
                                <StatLine
                                    label="Emails envoyés"
                                    value={intFormatter.format(data.mailjet?.total ?? 0)}
                                />
                                <StatLine
                                    label="Succès"
                                    value={intFormatter.format(data.mailjet?.success ?? 0)}
                                />
                                <StatLine
                                    label="Échecs"
                                    value={intFormatter.format(data.mailjet?.failure ?? 0)}
                                />
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Taux de réussite
                                    </Typography>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.max(0, Math.min(100, (mailjetSuccessRate || 0) * 100))}
                                        sx={{ mt: 0.5, borderRadius: 999 }}
                                    />
                                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                                        {percentFormatter.format(mailjetSuccessRate || 0)}
                                    </Typography>
                                </Box>
                                <StatLine
                                    label="Durée moyenne"
                                    value={`${decimalFormatter.format(data.mailjet?.avgDurationMs ?? 0)} ms`}
                                />
                            </ObservabilityCard>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ObservabilityCard title="Gemini">
                                <StatLine
                                    label="Appels"
                                    value={intFormatter.format(data.gemini?.total ?? 0)}
                                />
                                <StatLine
                                    label="Succès"
                                    value={intFormatter.format(data.gemini?.success ?? 0)}
                                />
                                <StatLine
                                    label="Échecs"
                                    value={intFormatter.format(data.gemini?.failure ?? 0)}
                                />
                                <StatLine
                                    label="Durée moyenne"
                                    value={`${decimalFormatter.format(data.gemini?.avgDurationMs ?? 0)} ms`}
                                />
                                <StatLine
                                    label="Taille moyenne des triplets"
                                    value={decimalFormatter.format(data.gemini?.avgResultSize ?? 0)}
                                    hint="items / appel"
                                />
                            </ObservabilityCard>
                        </Grid>
                    </Grid>
                ) : null}
            </Stack>
        </Box>
    );
}
